// The warning before an idle sign-out (Session ASB review, finding F4). Mounted once in OfficeLayout, and in the engineer
// app's shell (EngineerLayout) for an account there that has an idle limit (the review of the fixes, finding 4).
//
// WHY. Office and admin sessions end after 60 minutes with no activity (D-SEC-4, server/src/modules/auth/sessionStore.ts
// SESSION_LIMITS), and the first request after that is refused and sends the person to the sign-in page, taking any
// half-filled form with it. So about five minutes before the limit this says so, with one button that makes an ordinary
// request (touching the session) and so keeps them signed in. Timer-driven requests (the Messages and Lone worker polls)
// no longer count as activity (they carry X-ELE-Background), so an open polling page cannot hide the warning either.
//
// THE CLOCK IS RE-READ, NEVER SAMPLED (the platform rule, Session AQF). Idle time is worked out from Date.now() and the
// last activity every time it is checked: on a light interval, whenever the tab comes back into view (a hidden tab's
// timers are throttled or stopped), and whenever another tab of this browser records activity (they share one cookie,
// so one session: sessionEvents.ts keeps the last activity in localStorage). It is armed from one clock read at mount.
//
// Engineers have no idle limit, so the engineer app mounts this only for anybody else signed in there: in practice the
// Director's admin login, which he drives the app on (D90). The 12-hour absolute limit is not warned about.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { lastSessionActivityAt } from "../../lib/sessionEvents";
import { Button } from "../../components/Button";

// The office and admin idle limit, mirrored from the server (SESSION_LIMITS); IdleSessionWarning.test.tsx reads the
// server file and fails if the two drift apart.
export const OFFICE_IDLE_LIMIT_MS = 60 * 60_000;
export const IDLE_WARNING_BEFORE_MS = 5 * 60_000;
const CHECK_EVERY_MS = 15_000;

export type IdlePhase = "active" | "warning" | "ended";

// Pure: where a session idle since `lastActivityAt` stands at `now`.
export function idlePhase(now: number, lastActivityAt: number, limitMs: number = OFFICE_IDLE_LIMIT_MS): IdlePhase {
  const idleFor = now - lastActivityAt;
  if (idleFor >= limitMs) return "ended";
  if (idleFor >= limitMs - IDLE_WARNING_BEFORE_MS) return "warning";
  return "active";
}

// Where the warning sits (the review of the fixes, finding 4). "floating", the default and the office shell's: fixed at
// the foot of the screen, over the page. "banner", the engineer app's: a strip in the page flow under the header that
// sticks to the top of the screen as the page scrolls. The foot of the engineer app is taken by its tab bar and, on a
// job sheet, by the fixed submit bar, and a floating warning (z-50) would sit over both; offline, where "Stay signed
// in" cannot reach the server to take it away, it would stay over the submit button for good. The banner is painted
// on the shell's own ground, opaque in both themes, because once it sticks it lies over the page.
export type IdleWarningPlacement = "floating" | "banner";

const PLACEMENT_LAYOUT: Record<IdleWarningPlacement, string> = {
  floating: "fixed bottom-4 left-1/2 z-50 w-[min(92vw,28rem)] -translate-x-1/2 bg-ele-surface",
  banner: "sticky top-2 z-40 mx-4 mt-4 bg-[var(--ele-shell)]",
};

export function IdleSessionWarning({ placement = "floating" }: { placement?: IdleWarningPlacement } = {}) {
  // One clock read arms it: the shell mounting is activity too (it has just loaded the page).
  const [armedAt] = useState(() => Date.now());
  const [phase, setPhase] = useState<IdlePhase>("active");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(() => {
    setPhase(idlePhase(Date.now(), Math.max(lastSessionActivityAt(), armedAt)));
  }, [armedAt]);

  useEffect(() => {
    check();
    const t = setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("storage", check);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("storage", check);
    };
  }, [check]);

  // An ordinary request, which the server counts as activity (and api.ts records as such). If the session has already
  // ended, its 401 signs this browser out through the usual path and this shell goes with it.
  async function staySignedIn() {
    setBusy(true);
    setError(null);
    try {
      await api.me();
      check();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) {
        setError("Could not reach the server. Check your connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (phase === "active") return null;
  const ended = phase === "ended";
  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label={ended ? "Signed out after inactivity" : "You will be signed out soon"}
      className={`${PLACEMENT_LAYOUT[placement]} rounded-ele border border-[var(--ele-amber-text-deep)] px-4 py-3 text-sm text-ele-strong shadow-lg`}
    >
      <p>
        {ended
          ? "You have been signed out because you were inactive. Copy anything you have not saved, then sign in again."
          : "You will be signed out soon because you have been inactive."}
      </p>
      {error && (
        <p role="alert" className="mt-2 text-ele-danger">
          {error}
        </p>
      )}
      <Button className="mt-3" onClick={() => void staySignedIn()} disabled={busy}>
        {busy ? "Checking..." : ended ? "Sign in again" : "Stay signed in"}
      </Button>
    </div>
  );
}
